import {Badge} from '@/components/ui/badge/Badge'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {usePathologiesSearchableList} from '@/features/projects/hooks/usePathologiesSearchableList'
import {Diagnosis, DiagnosisVersionFilter} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {forwardRef, useRef, useState} from 'react'

interface InputSelectPathologyProps {
    label: string
    placeholder?: string
    value?: Diagnosis | null
    onChange: (value: Diagnosis | null) => void
    errorMessage?: string
    disabledOptions?: string[]
    disabled?: boolean
    /** When true, renders a clear (X) button inside the trigger to reset the selection. */
    isClearable?: boolean
    /** Classification to search in. Defaults to the one currently in force. */
    version?: DiagnosisVersionFilter
}

export const InputSelectPathology = forwardRef<HTMLSelectElement, InputSelectPathologyProps>(
    ({label, placeholder, value, onChange, errorMessage, disabledOptions, disabled, isClearable, version}, ref) => {
        const {t} = useTranslation()
        const [isQueryEnabled, setIsQueryEnabled] = useState(false)
        const inputRef = useRef(null)
        const {pathologies, isLoading, isError, onSearch, onResetSearch, hasNextPage, fetchNextPage} =
            usePathologiesSearchableList({
                limit: 10,
                enabled: isQueryEnabled,
                version
            })

        // Only worth the noise when the list can actually mix versions: on a single-version search
        // every row would carry the same badge, and the field label already states the version.
        const showVersionBadge = version == 'all'

        return (
            <ComboboxProvider setValue={onSearch} resetValueOnHide>
                <SelectProvider
                    value={value?.id ?? ''}
                    setValue={value => onChange(pathologies.find(pathology => value == pathology.id) ?? null)}
                    setOpen={isOpen => {
                        if (disabled) return
                        setIsQueryEnabled(isOpen)
                        onResetSearch(inputRef)
                    }}
                >
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <Label>{label}</Label>
                        <div style={{position: 'relative', width: '100%'}}>
                            <SelectTrigger
                                ref={ref}
                                placeholder={placeholder}
                                fullWidth
                                value={value?.id}
                                disabled={disabled}
                                displayValue={() =>
                                    isClearable ? (
                                        // single line + ellipsis, reserving room on the right so long values
                                        // never overlap the clear (X) button
                                        <span
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                textAlign: 'left',
                                                marginRight: 32
                                            }}
                                        >
                                            {`${value?.code} - ${value?.description}`}
                                        </span>
                                    ) : (
                                        `${value?.code} - ${value?.description}`
                                    )
                                }
                                hasError={!!errorMessage}
                            />
                            {/* Never on a disabled select: it would offer the one edit a read-only
                                field must not allow. */}
                            {isClearable && value && !disabled && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    shape="square"
                                    aria-label={t('commons:remove')}
                                    onPointerDown={event => event.stopPropagation()}
                                    onClick={event => {
                                        event.stopPropagation()
                                        onChange(null)
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: 30,
                                        transform: 'translateY(-50%)',
                                        height: 24,
                                        width: 24,
                                        minWidth: 24,
                                        padding: 0
                                    }}
                                >
                                    <XCloseIcon size={16} />
                                </Button>
                            )}
                        </div>
                        {errorMessage && (
                            <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                                <InputStatusIcon hasError={!!errorMessage} />
                                {errorMessage && <InputHelpText error={errorMessage} />}
                            </Flexbox>
                        )}
                        <SelectComboboxPopover sameWidth>
                            <SelectComboboxInput ref={inputRef} placeholder={t('commons:search_placeholder')} />
                            <SelectComboboxList>
                                {isLoading ? (
                                    <>
                                        {Array.from({length: 6}, (_, index) => (
                                            <Skeleton key={index} height={32} />
                                        ))}
                                    </>
                                ) : isError ? (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                ) : pathologies.length > 0 ? (
                                    <SelectVirtualList
                                        data={pathologies}
                                        style={{height: 200}}
                                        totalCount={25}
                                        overscan={100}
                                        endReached={() => hasNextPage && fetchNextPage()}
                                        itemContent={(itemIndex, item) => (
                                            <SelectItem
                                                key={itemIndex}
                                                value={item.id}
                                                disabled={disabledOptions?.includes(item.id)}
                                                hasItemCheckbox
                                            >
                                                <Flexbox gap={2} align="center">
                                                    {showVersionBadge && item.version && (
                                                        <Badge size="sm" shape="rectangle">
                                                            {item.version}
                                                        </Badge>
                                                    )}
                                                    <Text size="textSm" style={{minWidth: 50}}>
                                                        {item.code}
                                                    </Text>
                                                    <Text palette="neutral" shade="500" size="textSm">
                                                        {item.description}
                                                    </Text>
                                                </Flexbox>
                                            </SelectItem>
                                        )}
                                    />
                                ) : (
                                    <InputHelpText helpText={t('commons:no_results')} />
                                )}
                            </SelectComboboxList>
                        </SelectComboboxPopover>
                    </Flexbox>
                </SelectProvider>
            </ComboboxProvider>
        )
    }
)
